// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, Link } from 'wouter';
import ProgressBar from '../components/ProgressBar';
import { Button } from '../components/ui/button';
import { uploadPDF, startAnalysis, getProjects, telechargerRapport, telechargerExcel,
         getPreferences, marquerResultatVu } from '../services/api';
import { initSocket, subscribeToAnalysis } from '../services/socket';
import { useAuth } from '../context/AuthContext';
import { Paperclip, Plus, ChevronRight, Check, FolderOpen } from 'lucide-react';
import { useI18n } from '../i18n';
import { requestNotificationPermission, notifyAnalysisComplete, updatePageTitle } from '../services/notifications';
import { APP_VERSION } from '../data/changelog';
import { choixDeNormeAffiche, normesProposees } from '../lib/acces';

const Dashboard = () => {
  const [, setLocation] = useLocation();
  const [socket, setSocket] = useState(null);
  const { refreshUser, user } = useAuth();
  const { t, lang } = useI18n();

  // Quota épuisé : le serveur refuse déjà l'upload (403 quota_exceeded), mais
  // l'utilisateur ne l'apprenait qu'en essayant — et découvrait un « ❌ Erreur:
  // quota_exceeded » en anglais. On le prévient AVANT.
  //
  // Ne concerne que les comptes gouvernés par un quota : les rôles internes, les
  // forfaits (facturation à l'usage) et les licences (pool) ont `quota_remaining`
  // absent ou non pertinent — même règle que `verifier_droit_a_consommer()` côté
  // serveur. En cas de doute, on n'affiche RIEN : un faux blocage est pire qu'un
  // refus tardif.
  const quotaExhausted = (
    !['admin', 'dev'].includes(user?.role)
    && !user?.pricing_tier_id
    && !user?.license_id
    && user?.quota_remaining != null
    && user.quota_remaining <= 0
  );

  // Droits suspendus par le serveur (`access_warning.bloquant`) : paiement en
  // échec, abonnement résilié… ⚠️ `quotaExhausted` ne le voit PAS pour un
  // abonné en `past_due`, qui garde son `pricing_tier_id` : le dépôt restait
  // ouvert et le refus arrivait après coup, en « Erreur: subscription_past_due »
  // (constaté au test de Marc du 22/09/2026).
  const droitsSuspendus = !!user?.access_warning?.bloquant;

  // Normes que le compte peut lancer (T2). `null` = liste inconnue (profil pas
  // encore rafraîchi) : on ne bloque rien, le serveur tranche. Liste VIDE =
  // aucune norme activée : le dépôt est fermé, avec un message qui dit quoi faire.
  // Le choix n'est montré que s'il y en a plusieurs (`choixDeNormeAffiche`).
  const normes = normesProposees(user);
  const [normeChoisie, setNormeChoisie] = useState(null);
  const normeDuDepot = normeChoisie && normes?.includes(normeChoisie)
    ? normeChoisie
    : (normes && normes.length ? normes[0] : null);
  const aucuneNorme = !!normes && normes.length === 0;

  const depotBloque = quotaExhausted || droitsSuspendus || aucuneNorme;

  // Abonné dont le forfait mensuel est consommé. ⚠️ Il n'est PAS bloqué — ses
  // analyses passent et sont facturées à l'unité. Mais il doit l'apprendre ICI,
  // avant de déposer, et non sur sa facture : c'est la contrepartie de « ne
  // jamais bloquer ». L'information n'existait que dans le modal de profil,
  // qu'il faut ouvrir pour voir.
  const forfaitEpuise = (
    !['admin', 'dev'].includes(user?.role)
    && user?.tier_analyses_incluses != null
    && user?.tier_analyses_restantes === 0
  );
  const analysesHorsForfait = user?.tier_analyses_hors_forfait || 0;

  // Membre d'une licence Entreprise (hors Cabinet, qui a le bandeau du forfait
  // ci-dessus) dont le pool est consommé : même principe — rien n'est bloqué
  // (28/09/2026), les analyses suivantes sont facturées à l'organisation, et le
  // membre doit le savoir avant de déposer. Aucun prix ici : c'est l'affaire de
  // l'administrateur, qui le voit dans son portail et par e-mail.
  const poolLicenceEpuise = (
    !['admin', 'dev'].includes(user?.role)
    && user?.license
    && !user.license.facturation_stripe
    && user.license.pool_analyses_count != null
    && (user.license.pool_used ?? 0) >= user.license.pool_analyses_count
  );


  // ✅ NOUVEAU : Charger depuis sessionStorage au démarrage (chantier ⑮ : l’onglet seul, jamais le disque)
  const [currentAnalysis, setCurrentAnalysis] = useState(() => {
    const saved = sessionStorage.getItem('normascan_current_analysis');
    return saved ? JSON.parse(saved) : null;
  });
  // ✅ NOUVEAU : Persist uploadedFile (analyse uploadée mais pas démarrée)
  const [uploadedFile, setUploadedFile] = useState(() => {
    const saved = sessionStorage.getItem('normascan_pending_upload');
    return saved ? JSON.parse(saved) : null;
  });
  // État pour gérer la validation
  const [pdfValidation, setPdfValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const [progress, setProgress] = useState(() => {
    const saved = sessionStorage.getItem('normascan_progress');
    return saved ? JSON.parse(saved) : null;
  });
  
  
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const verifiedRef = useRef(false); // ✅ AJOUTER
  const [substep, setSubstep] = useState(null);
  const [outputType, setOutputType] = useState('both'); // ✅ AJOUT
  // Présélection = le format choisi dans Paramètres → Préférences (révision
  // 0023), qui suit le compte d'un poste à l'autre. Il reste modifiable pour ce
  // dépôt-ci. Une réponse absente laisse « les deux », comme avant.
  useEffect(() => {
    getPreferences()
      .then(p => { if (['both', 'html', 'excel'].includes(p.format_sortie_defaut)) setOutputType(p.format_sortie_defaut); })
      .catch(() => {});
  }, []);
  // ⚠️ Persisté, comme `uploadedFile`. Sans cela, un rechargement de page
  // restaurait le fichier déposé mais PAS le projet : l'analyse partait sans
  // `project_id`, donc sans contexte projet et sans qu'aucune garde ne
  // s'applique — ni côté client, ni côté serveur. L'utilisateur, lui, avait
  // bien sélectionné son projet et n'avait aucun moyen de voir la différence.
  const [selectedProjectId, setSelectedProjectId] = useState(
    () => sessionStorage.getItem('normascan_selected_project') || ''
  );
  const [projectsList, setProjectsList] = useState([]);
  const [selectedProjectStatus, setSelectedProjectStatus] = useState(null); // 'draft' | 'active' | null
  // Documents du projet en cours d'extraction par le LLM — calculé par le
  // serveur (`extraction_in_progress`), jamais déduit ici.
  const [selectedProjectExtracting, setSelectedProjectExtracting] = useState(false);
  const [startHovered, setStartHovered] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [projectPanelOpen, setProjectPanelOpen] = useState(false);
  const plusMenuRef = useRef(null);
  const projectPanelRef = useRef(null);
  const selectedProjectName = projectsList.find(p => p.id === selectedProjectId)?.name || '';

  // ✅ RAG V4.0 : pré-sélection de projet si navigation depuis la page Projets
  useEffect(() => {
    const stored = sessionStorage.getItem('normascan_preselect_project');
    if (!stored) return;
    try {
      const { id, name, status } = JSON.parse(stored);
      if (id) {
        setSelectedProjectId(id);
        setProjectsList(prev => prev.some(p => p.id === id) ? prev : [...prev, { id, name, status }]);
        if (status) setSelectedProjectStatus(status);
      }
    } catch (_) {}
    sessionStorage.removeItem('normascan_preselect_project');
  }, []); // run once on mount

  // La conversation de NORMA n'est restaurée au rechargement que si elle a
  // encore un objet : un fichier déposé ou une analyse en cours. Sinon (refus,
  // alertes, fin de parcours), la page repart de la salutation (Marc, 30/09/2026).
  const conversationEnCours = () =>
    !!(sessionStorage.getItem('normascan_pending_upload') || sessionStorage.getItem('normascan_current_analysis'));
  const [normaMessages, setNormaMessages] = useState(() => {
    if (!conversationEnCours()) return [];
    try { return JSON.parse(sessionStorage.getItem('norma_messages') || '[]'); } catch(_) { return []; }
  });
  const [normaCurrentText, setNormaCurrentText] = useState(() => {
    if (!conversationEnCours()) return '';
    return sessionStorage.getItem('norma_current_text') || '';
  });
  const [normaExpandedIdx, setNormaExpandedIdx] = useState(null);
  const prevPhaseRef = React.useRef('');
  const normaIntervalRef = useRef(null);
  const normaScrollRef = useRef(null);
  const normaUserScrolledRef = useRef(false);
  const normaCurrentTextRef = useRef('');
  const normaRestoredRef = useRef(false); // true = pas d'animation au restore

  // ── NORMA parle à la place des popups (Marc, 30/09/2026) ────────────────
  // Toute alerte de la page (dépôt refusé, fichier trop lourd, erreur
  // d'analyse, copie faite…) est ÉCRITE par NORMA dans sa bulle, jamais par
  // `alert()`. NORMA étant pilotée par des phases, une alerte est une phase de
  // plus (`alerte:<id>`), qui reste affichée tant que la phase « normale »
  // qu'elle a remplacée ne change pas (nouveau dépôt, progression, reset…) ;
  // elle passe alors dans l'historique comme les autres messages.
  // ⚠️ La phase d'appui est lue APRÈS le rendu (effet ci-dessous) : une alerte
  // émise en même temps qu'un `resetAnalysis()` s'accroche à l'état remis à
  // zéro, et ne disparaît pas aussitôt.
  const [normaAlerte, setNormaAlerte] = useState(null);   // { id, texte }
  const normaAlertesRef = useRef({});                      // id ➜ texte (archivage)
  const alerteEpingleeRef = useRef(null);                  // { id, sous, expiree }
  // Le texte COMPLET que NORMA est en train d'écrire. C'est lui qu'on archive
  // au changement de phase — jamais un message recalculé : le 30/09, le motif
  // précis d'un refus était archivé sous sa forme générique (l'état avait
  // changé entre-temps), d'où deux messages de refus à la suite.
  const messageAfficheRef = useRef('');
  const annulationEnCoursRef = useRef(false);   // cf handleError / handleCancelAnalysis
  const direNorma = (texte) => {
    if (!texte) return null;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    normaAlertesRef.current[id] = String(texte);
    alerteEpingleeRef.current = null;
    setNormaAlerte({ id, texte: String(texte) });
    return id;
  };

  // Les questions oui/non, posées par NORMA avec deux boutons sous son message
  // (plus de `window.confirm`). Rend une promesse : true = oui, false = non,
  // null = question dépassée (une autre alerte l'a remplacée : erreur, fin
  // d'analyse…) — l'appelant ne répond alors rien, NORMA a changé de sujet.
  const [normaQuestion, setNormaQuestion] = useState(null);  // { id, resolve }
  const demanderNorma = (texte) => new Promise((resolve) => {
    const id = direNorma(texte);
    setNormaQuestion({ id, resolve });
  });
  const repondreNorma = (reponse) => {
    const question = normaQuestion;
    if (!question) return;
    setNormaQuestion(null);
    question.resolve(reponse);
  };

  const isAnalysisInProgress = 
  currentAnalysis && 
  currentAnalysis.status === 'PROCESSING' && 
  progress && 
  progress.progress < 100;

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (progress) {
      sessionStorage.setItem('normascan_progress', JSON.stringify(progress));
    }
  }, [progress]);

  // ✅ Charger la liste des projets au mount + quand un fichier est uploadé
  const chargerProjets = useCallback(() => {
    return getProjects().then(data => {
      setProjectsList(Array.isArray(data) ? data : (data?.projects || []));
    }).catch(() => setProjectsList([]));
  }, []);

  useEffect(() => { chargerProjets(); }, [uploadedFile, chargerProjets]);

  useEffect(() => {
    if (selectedProjectId) sessionStorage.setItem('normascan_selected_project', selectedProjectId);
    else sessionStorage.removeItem('normascan_selected_project');
  }, [selectedProjectId]);

  // ⚠️ La liste n'était rechargée qu'au montage et à l'upload. Pendant une
  // extraction elle restait donc figée sur l'état d'AVANT validation : le
  // panneau annonçait le projet comme sélectionnable alors qu'il ne l'était
  // plus. Tant qu'au moins un projet extrait, on relit — c'est cette liste, et
  // non un fetch par projet, qui pilote l'affichage (elle est disponible dès le
  // rendu, sans attente).
  const extractionDansLaListe = projectsList.some(p => p.extraction_in_progress);
  useEffect(() => {
    if (!extractionDansLaListe) return;
    const timer = setInterval(chargerProjets, 5000);
    return () => clearInterval(timer);
  }, [extractionDansLaListe, chargerProjets]);

  // ✅ RAG V4.0 : statut du projet sélectionné + extraction en cours (warnings bloquants)
  //
  // ⚠️ Le projet passe en `active` dès la validation, sans attendre la fin de
  // l'extraction de ses documents. Se fier au seul `status` laissait donc lancer
  // une analyse pendant l'extraction : le contexte projet, lu uniquement sur les
  // documents `done`, arrivait incomplet — sans erreur, sans trace, et facturé.
  // Le champ `extraction_in_progress` vient du serveur, qui porte la règle ;
  // il n'est jamais recalculé ici à partir de la liste des documents.
  //
  // Toujours refetch (plus de raccourci par `projectsList`) : la liste peut
  // dater, et l'entrée injectée par « Lancer une analyse » depuis la page
  // Projets ne porte que `{id, name, status}`.
  const lireProjetSelectionne = useCallback(() => {
    if (!selectedProjectId) return Promise.resolve();
    return fetch(`${API_BASE_URL}/api/projects/${selectedProjectId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        setSelectedProjectStatus(data?.status || null);
        setSelectedProjectExtracting(Boolean(data?.extraction_in_progress));
      })
      .catch(() => {
        setSelectedProjectStatus(null);
        // En cas de doute on ne bloque pas — même règle que côté serveur.
        setSelectedProjectExtracting(false);
      });
  }, [selectedProjectId, API_BASE_URL]);

  useEffect(() => {
    if (!selectedProjectId) {
      setSelectedProjectStatus(null);
      setSelectedProjectExtracting(false);
      return;
    }
    lireProjetSelectionne();
  }, [selectedProjectId, lireProjetSelectionne]);

  // L'extraction dure de quelques secondes à quelques minutes : tant qu'elle
  // tourne, on relit pour que le bouton se débloque seul, sans rechargement.
  useEffect(() => {
    if (!selectedProjectExtracting || !selectedProjectId) return;
    const timer = setInterval(lireProjetSelectionne, 5000);
    return () => clearInterval(timer);
  }, [selectedProjectExtracting, selectedProjectId, lireProjetSelectionne]);

  // ✅ Fermer le menu + et le panneau projets au click extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(e.target)) {
        setPlusMenuOpen(false);
        setProjectPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ✅ NOUVEAU : Sauvegarder uploadedFile
  useEffect(() => {
    if (uploadedFile) {
      sessionStorage.setItem('normascan_pending_upload', JSON.stringify(uploadedFile));
    } else {
      sessionStorage.removeItem('normascan_pending_upload');
    }
  }, [uploadedFile]);

  // ✅ NOUVEAU : Sauvegarder currentAnalysis dans sessionStorage
  useEffect(() => {
    if (currentAnalysis) {
      sessionStorage.setItem('normascan_current_analysis', JSON.stringify(currentAnalysis));
    } else {
      sessionStorage.removeItem('normascan_current_analysis');
    }
  }, [currentAnalysis]);

  useEffect(() => {
    sessionStorage.setItem('norma_messages', JSON.stringify(normaMessages));
  }, [normaMessages]);

  // L'historique de NORMA d'une analyse vit sur le SERVEUR
  // (`analyses.norma_messages`) — plus dans le navigateur (chantier ⑮) : c'est
  // de là que le relisent la page de l'analyse et un autre poste.
  const conversationComplete = () => [
    ...normaMessages,
    ...(normaCurrentTextRef.current ? [{ text: normaCurrentTextRef.current }] : [])
  ].filter(m => m && m.text && m.text.trim().length > 0);

  // `keepalive` : l'envoi survit à une navigation ou à la fermeture de l'onglet.
  const envoyerConversation = (sid, messages) => {
    fetch(`${API_BASE_URL}/api/analyses/${sid}/norma-messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages }),
      keepalive: true,
    }).catch(() => {});
  };

  // Sauvegarde au fil de l'eau, 2 s après le dernier changement.
  useEffect(() => {
    const sid = currentAnalysis?.analysis_id;
    if (!sid) return;
    const allMessages = conversationComplete();
    if (allMessages.length === 0) return;
    const timer = setTimeout(() => envoyerConversation(sid, allMessages), 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normaMessages, normaCurrentText, currentAnalysis?.analysis_id]);
  
  // ✅ Cleanup états invalides au mount
  useEffect(() => {
    // Si analyse existe mais est ERROR ou sans status valide
    if (currentAnalysis && !['PENDING', 'PROCESSING', 'COMPLETED'].includes(currentAnalysis.status)) {
      console.warn('⚠️ [DASHBOARD] État invalide détecté, nettoyage...');
      resetAnalysis();
    }
    
    // Si progress est à 100 mais currentAnalysis pas COMPLETED
    if (progress?.progress >= 100 && currentAnalysis?.status !== 'COMPLETED') {
      console.warn('⚠️ [DASHBOARD] Incohérence progress/status, nettoyage...');
      resetAnalysis();
    }
  }, []); // ✅ Une seule fois au mount

  // ✅ Vérifier si uploadedFile existe encore en backend
  useEffect(() => {
    const verifyUploadedFile = async () => {
      if (!uploadedFile) return;
      
      console.log('🔍 [DASHBOARD] Vérification uploadedFile:', uploadedFile.analysis_id);
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/analyses/${uploadedFile.analysis_id}`);
        
        if (!response.ok) {
          console.warn('⚠️ [DASHBOARD] Upload introuvable en backend, nettoyage...');
          setUploadedFile(null);
          sessionStorage.removeItem('normascan_pending_upload');
          return;
        }
        
        const data = await response.json();
        
        // Si l'analyse a été démarrée ou est en erreur, clear uploadedFile
        if (data.status !== 'PENDING') {
          console.log('ℹ️ [DASHBOARD] Upload n\'est plus PENDING, clear');
          setUploadedFile(null);
          sessionStorage.removeItem('normascan_pending_upload');
        }
        
      } catch (error) {
        console.error('❌ [DASHBOARD] Erreur vérification upload:', error);
        // En cas d'erreur, on garde l'upload
      }
    };

    verifyUploadedFile();
  }, []); // ✅ Une seule fois au mount

  // ✅ Vérifier validation status depuis backend
  useEffect(() => {
    const loadValidationStatus = async () => {
      if (!uploadedFile) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/analyses/${uploadedFile.analysis_id}`);
        
        if (response.ok) {
          const data = await response.json();
          
          // Si validation effectuée, reconstituer pdfValidation
          if (data.validation_status && data.validation_status !== 'pending') {
            const details = data.validation_details ? JSON.parse(data.validation_details) : {};
            
            setPdfValidation({
              valid: data.validation_status === 'valid',
              info: {
                level: data.validation_status === 'valid' ? 'success' : 'error',
                message: details.message || (data.validation_status === 'valid' ? t('doc_validated') : t('doc_invalid')),
                ...details
              }
            });
            
            console.log('✅ [DASHBOARD] Validation status chargé depuis DB:', data.validation_status);
          }
        }
      } catch (error) {
        console.error('❌ [DASHBOARD] Erreur chargement validation:', error);
      }
    };
    
    loadValidationStatus();
  }, [uploadedFile]); // Charger quand uploadedFile change

  // Init socket - PERSISTE entre pages
  useEffect(() => {
    const newSocket = initSocket();
    setSocket(newSocket);
    return () => {
      // Socket reste connecté
    };
  }, []);
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // ✅ Réabonner au socket si analyse en cours au mount ou quand analyse change
  useEffect(() => {
    if (socket && currentAnalysis && currentAnalysis.status !== 'COMPLETED') {
      console.log('🔄 [DASHBOARD] Réabonnement à l\'analyse en cours:', currentAnalysis.analysis_id);
      subscribeToAnalysis(currentAnalysis.analysis_id).catch(err => {
        console.error('❌ [DASHBOARD] Erreur réabonnement:', err);
      });
    }
  }, [socket, currentAnalysis]); // ✅ Dépendances complètes

  // Écouter events SocketIO
  useEffect(() => {
    if (!socket || !currentAnalysis) return;

    const handleProgress = (data) => {
      if (data.analysis_id === currentAnalysis.analysis_id) {
        console.log('📊 Progress:', data.progress, '%');
        setProgress(data);
        setSubstep(null);
      }
    };

    const handleSubstep = (data) => {
      if (data.analysis_id === currentAnalysis.analysis_id) {
        console.log('🔄 Substep:', data.substep);
        setSubstep(data.substep);
      }
    };

    const handleComplete = (data) => {
      if (data.analysis_id === currentAnalysis.analysis_id) {
        console.log('✅ Analyse terminée');

        setProgress({ progress: 100, current_step: t('step_done') });
        setSubstep(null);
        
        setCurrentAnalysis(prev => ({
          ...prev,
          status: 'COMPLETED',
          ...(data.data || {})
        }));
        // Vue en direct : le bandeau de suivi n'a pas à l'annoncer (chantier ⑮).
        marquerResultatVu(currentAnalysis.analysis_id);
        // À l'endroit où COMPLETED est reçu via socket :
        if (data.status === 'COMPLETED') {
          const meta = data.metadata ? JSON.parse(data.metadata) : {};
          notifyAnalysisComplete(data.filename || '', meta.conformity_score);
          updatePageTitle(true);
        }
        // ✅ RAFRAÎCHIR QUOTA APRÈS ANALYSE
        refreshUser().catch(err => {
          console.error('❌ Erreur refresh quota:', err);
        });
      }
    };

    const handleError = (data) => {
      // Annulation demandée d'ici : la route `cancel` émet aussi cet événement
      // (« Analyse annulée ») — c'est `handleCancelAnalysis` qui le dit, une fois.
      if (annulationEnCoursRef.current) return;
      if (data.analysis_id === currentAnalysis.analysis_id) {
        console.error('❌ Erreur:', data.error_message);
        resetAnalysis(false, { garderConversation: true });
        // Tel quel : le motif d'un arrêt (document d'une autre norme…) est déjà
        // rédigé pour l'utilisateur par le serveur.
        direNorma(data.error_message);
      }
    };

    socket.on('analysis_progress', handleProgress);
    socket.on('analysis_substep', handleSubstep);
    socket.on('analysis_complete', handleComplete);
    socket.on('analysis_error', handleError);

    return () => {
      socket.off('analysis_progress', handleProgress);
      socket.off('analysis_substep', handleSubstep);
      socket.off('analysis_complete', handleComplete);
      socket.off('analysis_error', handleError);
    };
  }, [socket, currentAnalysis]);

  // ✅ Écouter événement suppression depuis Analyses
  useEffect(() => {
    const handleAnalysisDeleted = (event) => {
      const { analysisId } = event.detail;
      
      console.log('📡 [DASHBOARD] Événement suppression reçu:', analysisId);
      
      // Si c'est notre uploadedFile, le clear
      if (uploadedFile && uploadedFile.analysis_id === analysisId) {
        console.log('🧹 [DASHBOARD] Upload supprimé depuis Analyses, nettoyage...');
        setUploadedFile(null);
        sessionStorage.removeItem('normascan_pending_upload');
      }
      
      // Si c'est notre currentAnalysis, le clear
      if (currentAnalysis && currentAnalysis.analysis_id === analysisId) {
        console.log('🧹 [DASHBOARD] Analyse en cours supprimée, nettoyage...');
        resetAnalysis();
      }
    };
    // Dans resetAnalysis() :
    updatePageTitle(false);
    window.addEventListener('analysis_deleted', handleAnalysisDeleted);
    
    return () => {
      window.removeEventListener('analysis_deleted', handleAnalysisDeleted);
    };
  }, [uploadedFile, currentAnalysis]);

  // ========================================
  // ✅ NOUVEAU : Vérification backend au mount (Fix restart backend)
  // ========================================
  useEffect(() => {
    const verifyAnalysisExists = async () => {
      // ✅ Vérifier une seule fois
      if (verifiedRef.current) {
        console.log('🔍 [DASHBOARD] Vérification déjà faite, skip');
        return;
      }
      // Si pas d'analyse en cours, rien à vérifier
      if (!currentAnalysis) return;

      // Marquer comme vérifié
      verifiedRef.current = true;
      
      // Si analyse complétée, OK
      if (currentAnalysis.status === 'COMPLETED') return;
      
      console.log('🔍 [DASHBOARD] Vérification analyse de l’onglet:', currentAnalysis.analysis_id);
      
      try {
        // Appeler backend pour vérifier si analyse existe
        const response = await fetch(`${API_BASE_URL}/api/analyses/${currentAnalysis.analysis_id}`);
        
        if (!response.ok) {
          console.warn('⚠️ [DASHBOARD] Analyse introuvable backend, nettoyage...');
          resetAnalysis(false, { garderConversation: true });
          direNorma(t('alert_interrupted'));
          return;
        }
        
        const data = await response.json();
        const backendStatus = data.status;
        
        console.log('📊 [DASHBOARD] État backend:', backendStatus, 'Progress:', data.progress);
        
        // ✅ Restaurer selon l'état backend
        if (backendStatus === 'COMPLETED') {
          setCurrentAnalysis(prev => ({ ...prev, status: 'COMPLETED' }));
          setProgress({ progress: 100, current_step: t('step_done') });
          marquerResultatVu(currentAnalysis.analysis_id);
        } else if (backendStatus === 'ERROR') {
          console.warn('⚠️ [DASHBOARD] Analyse en erreur backend, nettoyage...');
          resetAnalysis(false, { garderConversation: true });
          direNorma(t('alert_failed'));
        } else if (backendStatus === 'CANCELLED') {
          // Annulée AILLEURS pendant que cette page n'était pas ouverte — depuis
          // sa carte de « Mes analyses » ou sa page (01/10/2026). Sans cette
          // branche, NORMA l'affichait « en cours » pour toujours.
          resetAnalysis(false, { garderConversation: true });
          direNorma(t('alert_analysis_cancelled'));
        } else if (backendStatus === 'PROCESSING') {
          // ✅ Analyse active, restaurer COMPLÈTEMENT
          console.log('✅ [DASHBOARD] Analyse active, restauration progression...');
          
          setCurrentAnalysis({
            analysis_id: currentAnalysis.analysis_id,
            filename: data.filename,
            status: 'PROCESSING'
          });
          
          // La plus AVANCÉE des deux : la base retarde (pendant la passe 1,
          // l'avancement fin n'arrive que par SocketIO et n'est pas écrit en
          // base). Reprendre la valeur de la base faisait RECULER NORMA à chaque
          // retour sur la page — « Démarrage… » puis « Extraction… » répétés
          // (Marc, 30/09/2026).
          const avancementLocal = progress?.progress || 0;
          const avancementBase = data.progress || 0;
          setProgress(avancementLocal > avancementBase
            ? { ...progress, total_steps: 12 }
            : { progress: avancementBase, current_step: t('step_processing'), total_steps: 12 });
          
          // Réabonner au socket
          if (socket && socket.connected) {
            await subscribeToAnalysis(currentAnalysis.analysis_id);
            console.log('✅ Réabonné au socket');
          }
        }
        
      } catch (error) {
        console.error('❌ [DASHBOARD] Erreur vérification:', error);
      }
    };

    verifyAnalysisExists();
  }, [socket]); // ✅ Dépendances : se refait si currentAnalysis change

  // ✅ NOUVEAU : Réinitialiser ET nettoyer sessionStorage
  // `garderConversation` : remettre l'analyse à zéro SANS vider NORMA — c'est le
  // cas de tout arrêt que NORMA explique (erreur, document d'une autre norme,
  // annulation) : effacer sa conversation juste avant qu'elle dise pourquoi
  // faisait disparaître tout l'historique (Marc, 30/09/2026).
  const resetAnalysis = (archiveSession = false, { garderConversation = false } = {}) => {
    console.log('🧹 [DASHBOARD] Reset complet');

    // Sauvegarder la conversation sur le serveur, TOUT DE SUITE : remettre
    // `currentAnalysis` à zéro annule le minuteur de la sauvegarde au fil de
    // l'eau (effet ci-dessus), et les deux dernières secondes seraient perdues.
    const sid = currentAnalysis?.analysis_id;
    if (sid) {
      const allMessages = conversationComplete();
      if (allMessages.length > 0) envoyerConversation(sid, allMessages);
    }

    // Reset session courante — sauf si NORMA doit poursuivre sa conversation
    // (elle archivera son message en cours au changement de phase suivant).
    if (!garderConversation) {
      setNormaMessages([]);
      setNormaCurrentText('');
      setNormaExpandedIdx(null);
      normaCurrentTextRef.current = '';
      messageAfficheRef.current = '';
      prevPhaseRef.current = '';
      normaRestoredRef.current = false;
      if (normaIntervalRef.current) { clearInterval(normaIntervalRef.current); normaIntervalRef.current = null; }
      sessionStorage.removeItem('norma_messages');
      sessionStorage.removeItem('norma_current_text');  // ✅ AJOUTER
    }
    setCurrentAnalysis(null);
    setProgress(null);
    setSubstep(null);
    setUploadedFile(null);
    setSelectedProjectId('');
    setProjectsList([]);
    setSelectedProjectStatus(null);
    setSelectedProjectExtracting(false);
    sessionStorage.removeItem('normascan_current_analysis');
    sessionStorage.removeItem('normascan_progress');
    sessionStorage.removeItem('normascan_pending_upload');
    sessionStorage.removeItem('normascan_selected_project');
    verifiedRef.current = false;
  };

  // Upload
  const handleFileUpload = async (file) => {
    if (!file) return;
    try {
      console.log('📤 Upload:', file.name);
      const data = await uploadPDF(file, normeDuDepot);

      console.log('✅ Upload réussi:', data);
      // Le refus du dépôt PRÉCÉDENT ne doit pas survivre (la phase « erreur » de
      // NORMA passe avant « validation en cours »). Effacé DANS LE MÊME RENDU que
      // le nouveau fichier : effacé avant l'envoi, NORMA repassait par « inactif »
      // et redisait bonjour à chaque dépôt (constaté par Marc le 30/09/2026).
      setPdfValidation(null);
      setUploadedFile({
        analysis_id: data.analysis_id,
        filename: data.filename,
        status: 'PENDING'
      });

      // ✅ NOUVEAU : Valider la taille du PDF
      await validatePdfSize(data.analysis_id);

    } catch (error) {
      console.error('❌ Erreur upload:', error);

      // Droits suspendus (paiement en échec, abonnement résilié, licence…) :
      // le serveur renvoie le code de `_etat_droits_compte`, que `fetch` porte
      // dans `error.message` SANS `response.status` — le cas « quota » ci-dessous
      // ne le reconnaissait donc pas, et la popup montrait le code brut.
      const cleDroits = `access_warning_${error.message}`;
      if (t(cleDroits) !== cleDroits) {
        direNorma(t(cleDroits));
        refreshUser();   // fait apparaître le bandeau et grise le dépôt
        return;
      }

      // Norme refusée (T2) — AVANT le cas « quota » : un 403 NORME_NON_ACTIVEE
      // y serait lu comme un quota épuisé, et le message serait faux.
      const codeErreur = error.response?.data?.code;
      if (codeErreur === 'NORME_NON_ACTIVEE' || codeErreur === 'NORME_INCONNUE') {
        direNorma(t(codeErreur === 'NORME_NON_ACTIVEE' ? 'norme_non_activee' : 'norme_inconnue'));
        refreshUser();   // resynchronise la liste des normes affichée
        return;
      }

      // ✅ Quota épuisé — message traduit, jamais le code technique.
      // Le serveur renvoie `error: 'quota_exceeded'` ; sans ce cas, la popup
      // affichait « ❌ Erreur: quota_exceeded » (constaté le 27/07/2026).
      if (error.response?.status === 403
          || error.message.includes('quota_exceeded')
          || error.message.includes('Quota épuisé')) {
        direNorma(`**${t('quota_exhausted_title')}**\n\n${t('quota_exhausted_body')}`);
        refreshUser();   // rafraîchit le compteur affiché dans la sidebar
        return;
      }

      // ✅ Gérer erreur 413 explicitement
      if (error.response?.status === 413 || error.message.includes('413') || error.message.includes('trop volumineux')) {
        direNorma(t('alert_too_large'));
        return;
      }
      
      // ✅ Gérer erreur serveur générique
      if (error.response?.status === 500) {
        direNorma(t('alert_server_error'));
        return;
      }
      
      // ✅ Si erreur 400 avec existing_analysis_id
      if (error.message.includes('déjà une analyse en cours')) {
        try {
          // Essayer d'extraire l'ID de l'analyse existante de la réponse
          const match = error.response?.data?.existing_analysis_id;
          
          if (match) {
            console.log('🔄 Récupération analyse existante:', match);
            
            // Charger l'analyse via API
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.normascan.fr';
            const response = await fetch(`${API_BASE_URL}/api/analyses/${match}`);
            
            if (response.ok) {
              const analysis = await response.json();
              
              // Restaurer dans le state
              setCurrentAnalysis({
                analysis_id: analysis.id,
                filename: analysis.filename,
                status: analysis.status
              });
              
              if (analysis.status === 'PROCESSING') {
                setProgress({ progress: 0, current_step: t('step_in_progress'), total_steps: 12 });
                
                // Réabonner au socket
                if (socket && socket.connected) {
                  await subscribeToAnalysis(analysis.id);
                }
              }
              
              direNorma(t('alert_existing_analysis'));
              return;
            }
          }
        } catch (err) {
          console.error('❌ Erreur récupération analyse existante:', err);
        }
      }

      direNorma(`${t('err_prefix')}${error.message}`);
    }
  };

  // Drag & drop
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (isAnalysisInProgress) {
      direNorma(t('alert_analysis_in_progress'));
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    const pdfFile = files.find(f => f.type === 'application/pdf');

    if (!pdfFile) {
      direNorma(t('alert_pdf_only'));
      return;
    }

    await handleFileUpload(pdfFile);
    // ⚠️ `normeDuDepot` DOIT figurer ici : `handleFileUpload` la lit, et un
    // callback mémorisé sans elle garderait la norme du rendu où il a été créé —
    // un fichier glissé-déposé après un changement de norme partirait avec
    // l'ANCIENNE (défaut de T2, trouvé à la relecture du 30/09/2026).
  }, [isAnalysisInProgress, normeDuDepot]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleButtonClick = () => {
    if (!isAnalysisInProgress && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCancelUpload = async () => {
    if (!uploadedFile) return;
    
    const reponse = await demanderNorma(t('confirm_delete_upload'));
    if (reponse !== true) {
      if (reponse === false) direNorma(t('norma_reponse_non'));
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/analyses/${uploadedFile.analysis_id}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        setUploadedFile(null);
        sessionStorage.removeItem('normascan_pending_upload');
        direNorma(t('alert_file_deleted'));
      } else {
        const data = await response.json();
        direNorma(`${t('err_prefix')}${data.error}`);
      }
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      direNorma(`${t('err_prefix')}${error.message}`);
    }
  };

  // Fonction de validation après upload
  const validatePdfSize = async (analysisId) => {
    setIsValidating(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/validate-pdf/${analysisId}`);
      
      if (!response.ok) {
        console.error('❌ Validation API error:', response.status);
        // Si erreur serveur, on continue quand même (validation optionnelle)
        setPdfValidation({ valid: true, info: { level: 'warning', message: t('validation_unavailable') }});
        setIsValidating(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setPdfValidation(data);
        // Un scan accepté pour OCR : noté sur le fichier, qui le transmet à
        // l'analyse (`currentAnalysis`, persistée) — NORMA le dira au démarrage.
        if (data.valid && data.info?.scan) {
          setUploadedFile(prev => (prev ? { ...prev, scan: true } : prev));
        }
        
        // Non valide : c'est NORMA qui dit le motif (phase `error_content` /
        // `error_size`, qui lit `pdfValidation.info.message`) — plus de popup.
        if (!data.valid) {
          // ✅ CLEAR uploadedFile car validation échouée
          setUploadedFile(null);
          sessionStorage.removeItem('normascan_pending_upload');
          
          // L'analyse est maintenant marquée ERROR en DB
          // User peut uploader un nouveau fichier
        }
      } else {
        console.error('Erreur validation:', data.error);
      }
    } catch (error) {
      console.error('Erreur validation PDF:', error);
    } finally {
      setIsValidating(false);
    }
  };

  // Démarrer l'analyse
  const handleStartAnalysis = async () => {
    if (!uploadedFile) return;

    // Garde AVANT toute mise en état « analyse en cours » : une fois
    // `currentAnalysis` posé, il est persisté en sessionStorage et l'application
    // le restaure au rechargement. C'est ce qui produisait l'écran figé sur
    // « Démarrage de l'analyse… » lors des refus (constaté le 27/07/2026).
    if (projectExtracting) {
      direNorma(t('project_extraction_wait'));
      return;
    }

    try {
      console.log('🎯 [DASHBOARD] Début handleStartAnalysis');
      console.log('🎯 [DASHBOARD] Analysis ID:', uploadedFile.analysis_id);
      console.log('🎯 [DASHBOARD] Socket:', socket ? 'Initialisé' : 'NON initialisé');
      console.log('🎯 [DASHBOARD] Socket connected:', socket?.connected);

      if (!socket || !socket.connected) {
        console.log('⏳ [DASHBOARD] Socket pas connecté, reconnexion...');
        
        if (socket && !socket.connected) {
          socket.connect();
        }
        
        let attempts = 0;
        while ((!socket || !socket.connected) && attempts < 50) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        
        if (!socket || !socket.connected) {
          throw new Error(t('alert_reload_page'));
        }
        
        console.log('✅ [DASHBOARD] Socket maintenant connecté');
      }

      console.log('📡 [DASHBOARD] Abonnement à l\'analyse...');
      await subscribeToAnalysis(uploadedFile.analysis_id);
      console.log('✅ [DASHBOARD] Abonné avec succès');
      
      setCurrentAnalysis({ ...uploadedFile, status: 'PROCESSING' });
      setProgress({ progress: 0, current_step: t('step_starting'), total_steps: 12 });
      setSubstep(null);
      
      console.log('🚀 [DASHBOARD] Démarrage de l\'analyse via API...');
      const result = await startAnalysis(uploadedFile.analysis_id, {
        render_html:  outputType !== 'excel',
        render_excel: outputType !== 'html',
        ...(selectedProjectId ? { project_id: selectedProjectId } : {}),
      });
      console.log('✅ [DASHBOARD] API start-analysis réponse:', result);
      
      setUploadedFile(null);
      sessionStorage.removeItem('normascan_pending_upload'); // ✅ AJOUTER ICI
    
    } catch (error) {
      console.error('❌ [DASHBOARD] Erreur complète:', error);

      // Refus transitoire : l'extraction du projet a démarré entre la dernière
      // relecture et le clic. Le PDF déposé reste valide et l'analyse existe
      // toujours en base — `resetAnalysis()` effacerait le fichier déposé et
      // le projet sélectionné, obligeant à tout redéposer pour attendre 30 s.
      // On revient simplement à l'écran d'avant le clic.
      if (error.code === 'project_extraction_in_progress') {
        setCurrentAnalysis(null);
        setProgress(null);
        setSubstep(null);
        sessionStorage.removeItem('normascan_current_analysis');
        sessionStorage.removeItem('normascan_progress');
        setSelectedProjectExtracting(true);
        direNorma(t('project_extraction_wait'));
        return;
      }

      // Le rapport déposé est DÉJÀ un document de ce projet (même empreinte).
      // Même raisonnement que ci-dessus : le PDF reste valide, seule la
      // composition du projet est à corriger — ne pas effacer le dépôt.
      if (error.code === 'project_doc_is_same_report') {
        setCurrentAnalysis(null);
        setProgress(null);
        setSubstep(null);
        sessionStorage.removeItem('normascan_current_analysis');
        sessionStorage.removeItem('normascan_progress');
        direNorma(t('project_doc_is_same_report'));
        return;
      }

      resetAnalysis(false, { garderConversation: true });
      direNorma(`❌ ${t('err_prefix')}${error.message}`);
    }
  };

  const handleCancelAnalysis = async () => {
    const reponse = await demanderNorma(t('confirm_cancel_analysis'));
    if (reponse !== true) {
      if (reponse === false) direNorma(t('norma_reponse_non'));
      return;
    }
    
    annulationEnCoursRef.current = true;
    try {
      const analysisId = currentAnalysis?.analysis_id || currentAnalysis?.id;

      const response = await fetch(`${API_BASE_URL}/api/analyses/${analysisId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (response.ok) {
        resetAnalysis(false, { garderConversation: true });
        direNorma(t('alert_analysis_cancelled'));
      } else {
        const data = await response.json();
        direNorma(`${t('err_prefix')}${data.error}`);
      }
    } catch (error) {
      console.error('❌ Erreur annulation:', error);
      direNorma(`${t('err_prefix')}${error.message}`);
    } finally {
      annulationEnCoursRef.current = false;
    }
  };

  const handleCopyResume = () => {
    let meta = {};
    try { meta = currentAnalysis?.metadata ? JSON.parse(currentAnalysis.metadata) : {}; } catch(_) {}
    const total = meta.total_samples;
    const positive = meta.positive_samples;
    let text = `Analyse Normascan — ${currentAnalysis.filename}\n`;
    text += `${t('copy_date')} : ${new Date().toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR')}\n\n`;
    if (total != null) {
      text += `${t('copy_samples')} : ${total}`;
      if (positive > 0) text += `, ${t('err_prefix').replace(':', '')} ${positive} ${positive > 1 ? t('copy_positives') : t('copy_positive')}`;
      else if (positive === 0) text += `, ${t('copy_all_negative')}`;
      text += `.\n`;
    }
    text += `\n${t('copy_footer')}`;
    navigator.clipboard.writeText(text).then(() => direNorma(t('alert_copy_done')));
  };
  const handleCopyResumeFromSession = (session) => {
    let text = `Analyse Normascan — ${session.filename}\n`;
    text += `${session.name}\n\n`;
    text += `\n${t('copy_footer')}`;
    navigator.clipboard.writeText(text).then(() => direNorma(t('alert_copy_short')));
  };
  // La phase affichée : l'alerte de NORMA tant que la phase « normale » sur
  // laquelle elle s'est posée n'a pas changé, sinon la phase normale.
  const getNormaPhase = () => {
    // Une question en attente de réponse ne s'efface pas : pendant une analyse,
    // la progression changerait de phase avant que l'utilisateur ait répondu.
    if (normaQuestion && normaAlerte && normaQuestion.id === normaAlerte.id) {
      return `alerte:${normaAlerte.id}`;
    }
    const sous = getPhaseSousJacente();
    const ep = alerteEpingleeRef.current;
    if (normaAlerte && !(ep && ep.id === normaAlerte.id && (ep.expiree || ep.sous !== sous))) {
      return `alerte:${normaAlerte.id}`;
    }
    if (ep && normaAlerte && ep.id === normaAlerte.id) ep.expiree = true;  // ne revient jamais
    return sous;
  };

  const getPhaseSousJacente = () => {
    if (currentAnalysis?.status === 'COMPLETED') return 'completed';
    if (currentAnalysis?.status === 'PROCESSING') {
      const p = progress?.progress || 0;
      if (p < 10)  return 'processing_start';
      if (p < 30)  return 'processing_extract';
      if (p < 50)  return 'processing_data';
      if (p < 65)  return 'processing_validate';
      if (p < 80)  return 'processing_ml';
      if (p < 90)  return 'processing_conformity';
      if (p < 95)  return 'processing_html';
      return 'processing_final';
    }
    if (currentAnalysis?.status === 'PENDING') return 'processing_start'; // ✅ AJOUT
    if (pdfValidation?.valid === false) {
      // ⚠️ `/api/validate-pdf` pose `validation_type` à la RACINE de sa réponse ;
      // la relecture en base (`loadValidationStatus`) le met dans `info`. Ne lire
      // que `info` classait tout refus frais en « taille » (NORMA disait « ce
      // document dépasse la limite » sur un rapport plomb), puis la relecture le
      // reclassait en « contenu » : deux phases, deux messages (Marc, 30/09/2026).
      const typeRefus = pdfValidation.validation_type || pdfValidation.info?.validation_type;
      return typeRefus === 'size' ? 'error_size' : 'error_content';
    }
    if (isValidating) return 'validating';
    if (uploadedFile && pdfValidation?.valid) return 'ready';
    if (uploadedFile) return 'uploaded';
    return 'idle';
  };

  const getNormaMessage = (phase) => {
    if (phase && phase.startsWith('alerte:')) {
      return normaAlertesRef.current[phase.slice('alerte:'.length)] || '';
    }
    const messages = {
      idle:                t('norma_idle'),
      uploaded:            t('norma_uploaded'),
      validating:          t('norma_validating'),
      // Scan : NORMA prévient qu'elle devra d'abord le lire par OCR (Marc,
      // 30/09/2026) — l'arrêt éventuel d'un scan d'une autre norme n'est plus
      // une surprise.
      ready:               (pdfValidation?.info?.scan || uploadedFile?.scan) ? t('norma_ready_scan') : t('norma_ready'),
      // Le motif PRÉCIS du refus, rendu par le serveur (« ce document semble
      // être un diagnostic plomb… », scan sans OCR, mission hors champ…) : c'est
      // NORMA qui le dit, plus une popup (Marc, 30/09/2026). Le texte générique
      // ne sert que si le serveur n'en donne pas.
      error_content:       pdfValidation?.info?.message || t('norma_err_content'),
      error_size:          pdfValidation?.info?.message || t('norma_err_size'),
      processing_start:    currentAnalysis?.scan ? t('norma_start_scan') : t('norma_start'),
      processing_extract:  t('norma_extract'),
      processing_data:     t('norma_data'),
      processing_validate: t('norma_validate'),
      processing_ml:       t('norma_ml'),
      processing_conformity: t('norma_conformity'),
      processing_html:     t('norma_html'),
      processing_final:    t('norma_final'),
      completed: (() => {
        let meta = {};
        try { meta = currentAnalysis?.metadata ? JSON.parse(currentAnalysis.metadata) : {}; } catch(_) {}
        const total = meta.total_samples;
        const positive = meta.positive_samples;
        const nb_c  = meta.nb_conformes     != null ? meta.nb_conformes     : null;
        const nb_r  = meta.nb_reserves      != null ? meta.nb_reserves      : null;
        const nb_nc = meta.nb_non_conformes != null ? meta.nb_non_conformes : null;
        let msg = '';
        if (selectedProjectName) {
          msg += `**${t('norma_project_prefix')}** ${selectedProjectName}\n\n`;
        }
        if (nb_c !== null || nb_r !== null || nb_nc !== null) {
          msg += `**${nb_c ?? 0} ${t('favorable_label')} · ${nb_r ?? 0} ${t('avec_reserve_label')} · ${nb_nc ?? 0} ${t('defavorable_label')}**\n\n`;
        } else {
          msg += `${t('conformity')}.\n\n`;
        }
        if (total != null) {
          msg += `${total} ${total > 1 ? t('samples_label_pl') : t('samples_label')} ${t('norma_analysed')}`;
          if (positive > 0) msg += `, ${t('norma_dont')} **${positive} ${positive > 1 ? t('copy_positives') : t('copy_positive')}**`;
          else if (positive === 0) msg += `, ${t('copy_all_negative')}`;
          msg += `.\n`;
        }
        const hasPdf = !!currentAnalysis?.report_html_path;
        const hasExcel = !!currentAnalysis?.report_excel_path;
        if (hasPdf || hasExcel) {
          msg += hasPdf && hasExcel ? '\n' + t('copy_both_ready') : hasPdf ? '\n' + t('copy_pdf_ready') : '\n' + t('copy_excel_ready');
        }
        return msg;
      })(),
    };
    return messages[phase] || messages.idle;
  };

  // Calculer la phase pendant le render — valeur stable entre renders si les inputs n'ont pas changé
  const currentNormaPhase = getNormaPhase();
  // Épingle une nouvelle alerte de NORMA sur la phase normale de l'état DÉJÀ
  // rendu (cf `direNorma`) : c'est ce rendu-là qui porte un éventuel reset.
  useEffect(() => {
    if (normaAlerte && alerteEpingleeRef.current?.id !== normaAlerte.id) {
      alerteEpingleeRef.current = { id: normaAlerte.id, sous: getPhaseSousJacente(), expiree: false };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normaAlerte]);
  // Restore sans animation au mount — DOIT être après getNormaPhase/getNormaMessage
  useEffect(() => {
    if (normaMessages.length > 0 || normaCurrentText || currentAnalysis) {
      const phase = getNormaPhase();
      const fullMessage = getNormaMessage(phase);
      prevPhaseRef.current = phase;
      messageAfficheRef.current = fullMessage;
      if (normaCurrentText && normaCurrentText.length >= fullMessage.length) {
        // Texte complet — restore sans animation
        normaRestoredRef.current = true;
        normaCurrentTextRef.current = normaCurrentText;
      } else if (normaCurrentText && normaCurrentText.length < fullMessage.length) {
        // Texte incomplet — reprendre l'animation depuis là où on s'est arrêté
        let i = normaCurrentText.length;
        normaCurrentTextRef.current = normaCurrentText;
        const speed = fullMessage.length > 150 ? 18 : 28;
        normaIntervalRef.current = setInterval(() => {
          i++;
          const slice = fullMessage.slice(0, i);
          normaCurrentTextRef.current = slice;
          setNormaCurrentText(slice);
          if (i >= fullMessage.length) { clearInterval(normaIntervalRef.current); normaIntervalRef.current = null; }
        }, speed);
      } else {
        // Pas de texte sauvegardé — afficher message complet sans animation
        normaRestoredRef.current = true;
        normaCurrentTextRef.current = fullMessage;
        setNormaCurrentText(fullMessage);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const message = getNormaMessage(currentNormaPhase);
    if (currentNormaPhase === prevPhaseRef.current) return;

    // Nouvelle phase, MÊME phrase : NORMA ne se répète pas (ni archive, ni
    // nouvelle frappe) — on note seulement la phase.
    if (message && message === messageAfficheRef.current) {
      prevPhaseRef.current = currentNormaPhase;
      return;
    }

    // Archiver le message précédent COMPLET (même si l'animation était en cours) :
    // celui qui était ÉCRIT, pas un recalcul de sa phase (cf `messageAfficheRef`).
    const prevPhase = prevPhaseRef.current;
    const textToArchive = messageAfficheRef.current || (prevPhase ? getNormaMessage(prevPhase) : null);

    prevPhaseRef.current = currentNormaPhase;
    messageAfficheRef.current = message;

    // Une question dépassée (une autre alerte l'a remplacée) : sans réponse.
    if (normaQuestion && currentNormaPhase !== `alerte:${normaQuestion.id}`) {
      repondreNorma(null);
    }

    if (normaIntervalRef.current) { clearInterval(normaIntervalRef.current); normaIntervalRef.current = null; }
    normaCurrentTextRef.current = '';
    setNormaCurrentText('');

    if (textToArchive && textToArchive.trim()) {
      setNormaMessages(prev => [...prev, { text: textToArchive }]);
    }

    // Pas d'animation au restore
    if (normaRestoredRef.current) {
      normaRestoredRef.current = false;
      normaCurrentTextRef.current = message;
      setNormaCurrentText(message);
      return;
    }

    let i = 0;
    const speed = message.length > 150 ? 12 : 28;
    normaIntervalRef.current = setInterval(() => {
      i++;
      const slice = message.slice(0, i);
      normaCurrentTextRef.current = slice;
      setNormaCurrentText(slice);
      if (i >= message.length) { clearInterval(normaIntervalRef.current); normaIntervalRef.current = null; }
    }, speed);
  }, [currentNormaPhase]); // ← une seule dépendance : la phase elle-même

  
  useEffect(() => {
    if (!normaUserScrolledRef.current && normaScrollRef.current) {
      normaScrollRef.current.scrollTop = normaScrollRef.current.scrollHeight;
    }
  }, [normaCurrentText, normaMessages]);

  const analysisId = currentAnalysis?.id || currentAnalysis?.analysis_id;

  // `fetch` + en-tête, jamais `?token=` dans l'URL (cf `telechargerFichier`).
  const handleDownload = (type) => {
    const telecharger = type === 'html' ? telechargerRapport : telechargerExcel;
    telecharger(analysisId).catch(e => direNorma(e.message));
  };
  

  // ✅ NOUVEAU : Composant d'alerte de validation
  const ValidationAlert = () => {
    if (!pdfValidation || !pdfValidation.info) return null;
    
    const { level, message, percent_used, pages, chars } = pdfValidation.info;
    
    const alertColors = {
      success: 'bg-green-50 border-l-4 border-green-500 text-green-800',
      warning: 'bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800',
      error: 'bg-red-50 border-l-4 border-red-500 text-red-800'
    };
    
    const alertIcons = {
      success: '✅',
      warning: '⚠️',
      error: '❌'
    };
    
    return (
      <div className={`p-4 mb-4 rounded-lg ${alertColors[level]}`}>
        <div className="flex items-start">
          <span className="text-2xl mr-3">{alertIcons[level]}</span>
          <div className="flex-1">
            <p className="font-medium whitespace-pre-line">{message}</p>
            {pages && (
              <div className="mt-2 text-sm opacity-80">
                <div className="flex items-center space-x-2">
                  <span>{pages} {t('val_pages')}</span>
                  <span>•</span>
                  <span>{chars?.toLocaleString()} {t('val_chars')}</span>
                  <span>•</span>
                  <span>{percent_used}% {t('val_limit_pct')}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  // ✅ Sélecteur type de sortie
  const OutputTypeSelector = () => (
    <div className="mb-3">
      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
        {t('output_label')}
      </p>
      <div className="flex gap-2 mb-3">
        {[
          { value: 'both',  label: t('output_both'),  desc: t('output_both_desc') },
          { value: 'html',  label: t('output_html'),  desc: t('output_html_desc') },
          { value: 'excel', label: t('output_excel'), desc: t('output_excel_desc') },
        ].map(opt => (
          <button
            key={opt.value}
            onClick={() => setOutputType(opt.value)}
            style={{
              flex: 1, padding: '10px 8px', borderRadius: 8, textAlign: 'center',
              border: `2px solid ${outputType === opt.value ? 'var(--blue-light)' : 'var(--gray)'}`,
              background: outputType === opt.value ? 'rgba(59,130,246,0.08)' : 'var(--white)',
              cursor: 'pointer', fontSize: 12,
              fontWeight: outputType === opt.value ? 700 : 400,
              color: outputType === opt.value ? 'var(--blue)' : 'var(--text-muted)',
              transition: 'all 0.15s',
            }}
          >
            <div>{opt.label}</div>
            <div style={{ fontSize: 10, marginTop: 2, opacity: 0.7 }}>{opt.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );

  // ✅ Sélecteur de projet (optionnel — contexte RAG V4.0)
  const ProjectSelector = () => null; // Remplacé par le menu + dans la barre de saisie

  const projectNotValidated = selectedProjectId && selectedProjectStatus === 'draft';
  // ⚠️ La liste D'ABORD, le fetch ensuite. `projectsList` porte déjà
  // `extraction_in_progress` et est disponible **au rendu** ; le fetch par
  // projet, lui, résout après coup. N'avoir que le second laissait le bouton
  // actif pendant tout le temps de la requête — c'est ce qui a permis de lancer
  // une analyse malgré la garde (constaté par Marc le 27/07/2026). Le `||`
  // garde le fetch comme source la plus fraîche quand la liste, elle, date.
  const projectExtracting = Boolean(
    selectedProjectId
    && (projectsList.find(p => p.id === selectedProjectId)?.extraction_in_progress
        || selectedProjectExtracting)
  );
  const canStartAnalysis = pdfValidation?.valid !== false && !isValidating
                           && !projectNotValidated && !projectExtracting;
  const isCompleted = currentAnalysis?.status === 'COMPLETED';

  const renderText = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i, arr) => {
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <span key={i}>
          {parts.map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p)}
          {i < arr.length - 1 && <br />}
        </span>
      );
    });
  };
  const firstLine = (text) => text ? text.split('\n')[0] : '';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>

      <style>{`
        @keyframes norma-blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes norma-dot { 0%,100%{transform:scale(0.7);opacity:0.3} 50%{transform:scale(1.2);opacity:1} }
        .norma-cursor { animation: norma-blink 0.8s infinite; display:inline-block; }
        .norma-dot { animation: norma-dot 1.2s infinite; }
        .norma-dot:nth-child(2){ animation-delay:0.4s; }
        .norma-dot:nth-child(3){ animation-delay:0.8s; }
        .norma-msg-archived:hover { background: rgba(128,128,128,0.06) !important; }
      `}</style>

      {/* Zone messages scrollable */}
      <div
        ref={normaScrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          normaUserScrolledRef.current = el.scrollHeight - el.scrollTop - el.clientHeight > 40;
        }}
        style={{ flex: 1, overflowY: 'auto', padding: '28px 24px 12px 24px', minHeight: 0 }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>

          {/* En-tête NORMA */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: 'white', flexShrink: 0 }}>N</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--black)' }}>NORMA</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Assistant IA · NF X46-020</div>
            </div>
            {isAnalysisInProgress && (
              <div style={{ marginLeft: 8, display: 'flex', gap: 4, alignItems: 'center' }}>
                {[0,1,2].map(i => <div key={i} className="norma-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--blue)' }} />)}
              </div>
            )}
            {isCompleted && <span style={{ marginLeft: 8, fontSize: 16 }}>✅</span>}
          </div>

          {/* Messages session courante archivés */}
          {normaMessages.filter(m => m && m.text && m.text.trim().length > 0).map((msg, idx) => (
            <div
              key={idx}
              className="norma-msg-archived"
              onClick={(e) => {
                e.stopPropagation();
                const scrollTop = normaScrollRef.current?.scrollTop;
                setNormaExpandedIdx(prev => prev === idx ? null : idx);
                requestAnimationFrame(() => { if (normaScrollRef.current) normaScrollRef.current.scrollTop = scrollTop; });
              }}
              style={{ padding: '8px 4px', borderRadius: 8, marginBottom: 2, cursor: 'pointer', transition: 'background 0.15s' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{
                  flex: 1, fontSize: 14, lineHeight: 1.75,
                  color: normaExpandedIdx === idx ? 'var(--black)' : 'var(--text-muted)',
                  overflow: normaExpandedIdx === idx ? 'visible' : 'hidden',
                  textOverflow: normaExpandedIdx === idx ? 'unset' : 'ellipsis',
                  whiteSpace: normaExpandedIdx === idx ? 'normal' : 'nowrap',
                }}>
                  {normaExpandedIdx === idx ? renderText(msg.text) : firstLine(msg.text)}
                </div>
                <span style={{ fontSize: 10, flexShrink: 0, color: 'var(--text-muted)', marginTop: 5 }}>
                  {normaExpandedIdx === idx ? '▲' : '▼'}
                </span>
              </div>
            </div>
          ))}

          {/* Message courant animé */}
          {normaCurrentText && (
            <div style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--black)', marginBottom: 16, marginTop: 4 }}>
              {renderText(normaCurrentText)}
              {normaCurrentText.length < messageAfficheRef.current.length && (
                <span className="norma-cursor" style={{ color: 'var(--blue)', fontWeight: 700 }}>▋</span>
              )}
              {/* Question oui/non de NORMA — boutons une fois la phrase écrite */}
              {normaQuestion
                && currentNormaPhase === `alerte:${normaQuestion.id}`
                && normaCurrentText.length >= messageAfficheRef.current.length && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button type="button" onClick={() => repondreNorma(true)}
                    style={{ padding: '7px 18px', borderRadius: 8, background: 'var(--blue)', color: 'white',
                             border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {t('norma_oui')}
                  </button>
                  <button type="button" onClick={() => repondreNorma(false)}
                    style={{ padding: '7px 18px', borderRadius: 8, background: 'transparent', color: 'var(--black)',
                             border: '1px solid var(--gray)', fontSize: 13, cursor: 'pointer' }}>
                    {t('norma_non')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Carte analyse en cours / terminée */}
          {currentAnalysis && progress && (
            <div style={{ marginTop: 8, padding: '16px', borderRadius: 12, border: '1px solid var(--gray)', background: 'rgba(128,128,128,0.03)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--black)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {currentAnalysis.status === 'COMPLETED' ? `✅ ${currentAnalysis.filename}` : `⚙️ ${currentAnalysis.filename}`}
                </span>
                {currentAnalysis.status === 'COMPLETED' ? (
                  <Button onClick={() => resetAnalysis(true)} variant="outline" size="sm">{t('btn_new_analysis')}</Button>
                ) : (
                  <Button onClick={handleCancelAnalysis} variant="destructive" size="sm">{t('btn_cancel')}</Button>
                )}
              </div>
              <ProgressBar progress={progress.progress || 0} currentStep={progress.current_step} substep={substep} />
              {currentAnalysis.status === 'COMPLETED' && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--gray)' }}>
                  {(() => {
                    let m = {};
                    try { m = currentAnalysis?.metadata ? JSON.parse(currentAnalysis.metadata) : {}; } catch(_) {}
                    const nc = m.nb_conformes, nr = m.nb_reserves, nnc = m.nb_non_conformes;
                    if (nc == null && nr == null && nnc == null) return null;
                    return (
                      <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: '#f9fafb', border: '1px solid #e5e7eb', display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', fontSize: 12 }}>
                        <span style={{ color: '#22c55e', fontWeight: 700 }}>{nc ?? 0} {t('favorable_label')}</span>
                        <span style={{ color: '#d1d5db' }}>·</span>
                        <span style={{ color: '#f59e0b', fontWeight: 700 }}>{nr ?? 0} {t('avec_reserve_label')}</span>
                        <span style={{ color: '#d1d5db' }}>·</span>
                        <span style={{ color: '#ef4444', fontWeight: 700 }}>{nnc ?? 0} {t('defavorable_label')}</span>
                      </div>
                    );
                  })()}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {currentAnalysis.report_html_path && (
                      <Button className="flex-1" onClick={() => handleDownload('html')}>{t('btn_dl_pdf')}</Button>
                    )}
                    {currentAnalysis.report_excel_path && (
                      <Button variant="outline" className="flex-1" onClick={() => handleDownload('excel')}>{t('btn_dl_excel')}</Button>
                    )}
                    <button
                      onClick={handleCopyResume}
                      style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid var(--gray)', color: 'var(--black)', fontSize: 12, cursor: 'pointer', flexShrink: 0, transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(128,128,128,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >{t('btn_copy_summary')} </button>
                  </div>
                  <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', fontSize: 11, color: '#78350f', lineHeight: 1.5 }}>
                    <strong>{t('disclaimer_title')}.</strong> {t('disclaimer_body')}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ── Barre de saisie bas ── */}
      <div style={{ borderTop: '1px solid var(--gray)', background: 'var(--bg-surface)', padding: '12px 24px 14px 24px', flexShrink: 0 }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>

          {/* Quota épuisé : bandeau explicite AVANT la zone de dépôt, qui est
              elle-même neutralisée. Le serveur refuse de toute façon (403), mais
              découvrir un refus après avoir choisi son fichier ET son type de
              rendu est une mauvaise expérience — c'est ce que vivait
              l'utilisateur avant le 27/07/2026. */}
          {/* Quota d'essai épuisé : l'encart ENTIER mène à l'abonnement.
              ⚠️ Ni rouge ni pictogramme d'interdit — c'est une invitation à
              souscrire, pas une sanction : le compte n'a rien fait de mal, il a
              fini son essai. La pulsation est lente et de faible amplitude pour
              attirer l'œil sans agresser (et se coupe sous
              `prefers-reduced-motion`, cf `index.css`). */}
          {quotaExhausted && !currentAnalysis && (
            <Link
              href="/parametres?tab=billing"
              className="encart-quota-epuise"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                border: '1.5px solid var(--blue)',
                borderRadius: 14,
                background: 'rgba(50,96,225,0.05)',
                padding: '12px 16px',
                marginBottom: 10,
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--black)' }}>
                  {t('quota_exhausted_title')}
                </div>
                <div style={{ fontSize: 13, color: 'var(--gray-dark)', marginTop: 2 }}>
                  {t('quota_exhausted_body')}
                </div>
              </div>
              <span className="encart-quota-fleche" style={{
                fontSize: 13, fontWeight: 600, color: 'var(--blue)', whiteSpace: 'nowrap',
              }}>
                {t('quota_exhausted_cta')} →
              </span>
            </Link>
          )}

          {/* Forfait consommé : information, pas blocage. Ambre et non rouge —
              rien n'est refusé, le client est seulement prévenu du tarif. */}
          {forfaitEpuise && !currentAnalysis && (
            <div style={{
              border: '1.5px solid #f59e0b',
              borderRadius: 14,
              background: 'rgba(245,158,11,0.07)',
              padding: '12px 16px',
              marginBottom: 10,
            }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--black)' }}>
                {t('forfait_epuise_title')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--gray-dark)', marginTop: 2 }}>
                {analysesHorsForfait > 0
                  ? `${analysesHorsForfait} ${t(analysesHorsForfait === 1
                      ? 'forfait_depasse_body_singular' : 'forfait_depasse_body_plural')}`
                  : t('forfait_epuise_body')}
              </div>
            </div>
          )}

          {poolLicenceEpuise && !currentAnalysis && (
            <div style={{
              border: '1.5px solid #f59e0b',
              borderRadius: 14,
              background: 'rgba(245,158,11,0.07)',
              padding: '12px 16px',
              marginBottom: 10,
            }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--black)' }}>
                {t('pool_licence_epuise_title')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--gray-dark)', marginTop: 2 }}>
                {t('pool_licence_epuise_body')}
              </div>
            </div>
          )}

          {aucuneNorme && !currentAnalysis && (
            <div style={{
              border: '1.5px solid #f59e0b',
              borderRadius: 14,
              background: 'rgba(245,158,11,0.07)',
              padding: '12px 16px',
              marginBottom: 10,
              fontSize: 13,
              color: 'var(--gray-dark)',
            }}>
              {t('depot_aucune_norme')}
            </div>
          )}

          {/* Choix de la norme (T2) : seulement s'il y en a PLUSIEURS d'activées —
              avec une seule, rien à choisir, rien à montrer (« clé en main »). */}
          {choixDeNormeAffiche(user) && !currentAnalysis && !uploadedFile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <label htmlFor="depot-norme" style={{ fontSize: 13, color: 'var(--gray-dark)' }}>
                {t('depot_norme_label')}
              </label>
              <select
                id="depot-norme"
                value={normeDuDepot || ''}
                onChange={(e) => setNormeChoisie(e.target.value)}
                disabled={isAnalysisInProgress}
                style={{
                  fontSize: 13, padding: '4px 8px', borderRadius: 8,
                  border: '1px solid var(--gray)', background: 'var(--white)', color: 'var(--black)',
                }}
              >
                {normes.map((code) => (
                  <option key={code} value={code}>{t(`norme_${code}`)}</option>
                ))}
              </select>
            </div>
          )}

          {!currentAnalysis && (
            <div
              onDrop={depotBloque ? (e) => e.preventDefault() : handleDrop}
              onDragOver={depotBloque ? (e) => e.preventDefault() : handleDragOver}
              onDragLeave={() => setIsDragging(false)}
              aria-disabled={depotBloque}
              style={{
                border: `1.5px solid ${isDragging && !depotBloque ? 'var(--blue-light)' : 'var(--gray)'}`,
                borderRadius: 14,
                background: isDragging && !depotBloque ? 'rgba(50,96,225,0.04)' : 'var(--white)',
                transition: 'all 0.15s',
                padding: uploadedFile ? '12px 14px' : '0',
                opacity: depotBloque ? 0.5 : 1,
                pointerEvents: depotBloque ? 'none' : 'auto',
              }}
            >
              {!uploadedFile ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                  <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileSelect} className="hidden" />

                  {/* Bouton + (Plus d'options) */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      onClick={() => { setPlusMenuOpen(v => !v); setProjectPanelOpen(false); }}
                      title={t('plus_options_tooltip')}
                      disabled={isAnalysisInProgress}
                      style={{
                        width: 34, height: 34, borderRadius: '50%',
                        background: selectedProjectId ? '#059669' : 'var(--bg-surface)',
                        color: selectedProjectId ? 'white' : 'var(--text-muted)',
                        border: selectedProjectId ? '1.5px solid #059669' : '1.5px solid var(--gray)',
                        cursor: isAnalysisInProgress ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, transition: 'all 0.2s', opacity: isAnalysisInProgress ? 0.5 : 1,
                      }}
                      onMouseEnter={e => { if (!isAnalysisInProgress && !selectedProjectId) { e.currentTarget.style.background = 'rgba(128,128,128,0.08)'; } }}
                      onMouseLeave={e => { if (!selectedProjectId) e.currentTarget.style.background = 'var(--bg-surface)'; }}
                    ><Plus size={18} /></button>

                    {/* Menu déroulant "Plus d'options" */}
                    {plusMenuOpen && (
                      <div ref={plusMenuRef} style={{
                        position: 'absolute', bottom: '100%', left: 0, marginBottom: 6,
                        background: 'var(--white)', border: '1px solid var(--gray)',
                        borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 200,
                        minWidth: 260, overflow: 'hidden',
                      }}>
                        {/* Item : Associer un projet (accordion toggle) */}
                        <div
                          onClick={() => setProjectPanelOpen(v => !v)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                            cursor: 'pointer', transition: 'background 0.12s',
                          }}
                          onMouseOver={e => e.currentTarget.style.background = 'rgba(128,128,128,0.06)'}
                          onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <FolderOpen size={15} style={{ color: selectedProjectId ? '#059669' : 'var(--text-muted)', flexShrink: 0 }} />
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: selectedProjectId ? '#059669' : 'var(--black)' }}>
                            {selectedProjectId ? selectedProjectName : t('plus_associate_project')}
                          </span>
                          <ChevronRight size={14} style={{
                            color: 'var(--text-muted)', flexShrink: 0,
                            transform: projectPanelOpen ? 'rotate(90deg)' : 'none',
                            transition: 'transform 0.15s',
                          }} />
                        </div>

                        {/* Liste projets en accordion inline */}
                        {projectPanelOpen && (
                          <div style={{ borderTop: '1px solid var(--gray)', maxHeight: 280, overflowY: 'auto' }}>
                            {/* Option "Aucun projet" pour dissocier */}
                            <div
                              onClick={() => { setSelectedProjectId(''); setPlusMenuOpen(false); setProjectPanelOpen(false); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                                cursor: 'pointer', transition: 'background 0.12s',
                                borderBottom: '1px solid var(--gray)',
                              }}
                              onMouseOver={e => e.currentTarget.style.background = 'rgba(128,128,128,0.06)'}
                              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <span style={{ flex: 1, fontSize: 13, color: !selectedProjectId ? '#059669' : 'var(--text-muted)', fontWeight: !selectedProjectId ? 600 : 400 }}>
                                {t('project_select_none')}
                              </span>
                              {!selectedProjectId && <Check size={15} style={{ color: '#059669', flexShrink: 0 }} />}
                            </div>

                            {projectsList.length === 0 && (
                              <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
                                {t('plus_no_projects')}
                              </div>
                            )}

                            {projectsList.map(p => {
                              const isSelected = selectedProjectId === p.id;
                              const isDraft = p.status === 'draft';
                              // Un projet dont les documents s'extraient n'est pas
                              // sélectionnable : le bloquer ici plutôt qu'au clic sur
                              // « Analyser » évite d'avoir à défaire une sélection, et
                              // l'information vient de la liste, donc sans latence.
                              const isExtracting = Boolean(p.extraction_in_progress);
                              const isLocked = isDraft || isExtracting;
                              return (
                                <div
                                  key={p.id}
                                  title={isExtracting ? t('project_not_selectable_extracting') : undefined}
                                  onClick={() => {
                                    if (isLocked) return;
                                    setSelectedProjectId(p.id);
                                    setPlusMenuOpen(false);
                                    setProjectPanelOpen(false);
                                  }}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                                    cursor: isLocked ? 'not-allowed' : 'pointer', transition: 'background 0.12s',
                                    opacity: isLocked ? 0.55 : 1,
                                  }}
                                  onMouseOver={e => { if (!isLocked) e.currentTarget.style.background = 'rgba(128,128,128,0.06)'; }}
                                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                >
                                  <span style={{
                                    flex: 1, fontSize: 13, fontWeight: isSelected ? 600 : 400,
                                    color: isSelected ? '#059669' : isLocked ? 'var(--text-muted)' : 'var(--black)',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  }}>
                                    {p.name}
                                  </span>
                                  {isDraft && (
                                    <span style={{ fontSize: 10, color: '#92400e', background: 'rgba(245,158,11,0.12)', padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>
                                      {t('plus_draft')}
                                    </span>
                                  )}
                                  {isExtracting && (
                                    <span style={{ fontSize: 10, color: '#1e40af', background: 'rgba(59,130,246,0.12)', padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>
                                      {t('project_status_processing')}
                                    </span>
                                  )}
                                  {isSelected && <Check size={15} style={{ color: '#059669', flexShrink: 0 }} />}
                                </div>
                              );
                            })}

                            {/* Message bloquant si sélection draft */}
                            {projectNotValidated && (
                              <div style={{ padding: '8px 12px', borderTop: '1px solid var(--gray)', background: 'rgba(245,158,11,0.06)', fontSize: 11, color: '#92400e', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                <span style={{ flexShrink: 0 }}>⚠️</span>
                                <span>{t('dashboard_project_not_validated')}</span>
                              </div>
                            )}

                            {/* Message bloquant si extraction des documents en cours */}
                            {projectExtracting && (
                              <div style={{ padding: '8px 12px', borderTop: '1px solid var(--gray)', background: 'rgba(59,130,246,0.06)', fontSize: 11, color: '#1e40af', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                <span style={{ flexShrink: 0 }}>⏳</span>
                                <span>{t('project_extraction_wait')}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleButtonClick}
                    title="Ajouter un fichier"
                    disabled={isAnalysisInProgress}
                    style={{
                      width: 34, height: 34, borderRadius: '30%',
                      background: 'var(--blue)', color: 'white',
                      border: 'none', cursor: isAnalysisInProgress ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, transition: 'background 0.15s', opacity: isAnalysisInProgress ? 0.5 : 1,
                    }}
                    onMouseEnter={e => { if (!isAnalysisInProgress) e.currentTarget.style.background = 'var(--blue-light)'; }}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--blue)'}
                  ><Paperclip size={16} /></button>
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--text-muted)'}}>
                    {droitsSuspendus
                      ? t('access_blocked_dropzone')
                      : quotaExhausted
                      ? t('quota_exhausted_dropzone')
                      : isDragging ? t('upload_dragging')
                      : selectedProjectId ? `${t('upload_placeholder')}  ·  ${selectedProjectName}`
                      : t('upload_placeholder')}
                  </span>
                </div>
              ) : (
                <div>
                  {isValidating && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: 'var(--blue)', fontSize: 13 }}>
                      <span>⏳</span>
                      <span>{t('upload_validating')}</span>
                    </div>
                  )}
                  {!isValidating && <ValidationAlert />}
                  {!isValidating && pdfValidation?.valid !== false && <OutputTypeSelector />}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>📄</span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--black)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                      {uploadedFile.filename}
                    </span>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button
                        onClick={handleCancelUpload}
                        style={{ padding: '7px 12px', borderRadius: 8, background: 'transparent', border: '1px solid #fca5a5', color: '#dc2626', fontSize: 12, cursor: 'pointer', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >{t('btn_delete')}</button>
                      <button
                        onClick={handleStartAnalysis}
                        disabled={!canStartAnalysis}
                        title={projectExtracting ? t('project_extraction_wait') : undefined}
                        onMouseEnter={() => setStartHovered(true)}
                        onMouseLeave={() => setStartHovered(false)}
                        style={{
                          padding: '7px 18px', borderRadius: 8,
                          background: startHovered && canStartAnalysis ? 'var(--blue-light)' : 'var(--blue)',
                          color: 'white', border: 'none', fontSize: 13, fontWeight: 600,
                          cursor: canStartAnalysis ? 'pointer' : 'not-allowed',
                          opacity: canStartAnalysis ? 1 : 0.5,
                          transform: startHovered && canStartAnalysis ? 'translateY(-1px)' : 'none',
                          boxShadow: startHovered && canStartAnalysis ? '0 4px 12px rgba(50,96,225,0.35)' : 'none',
                          transition: 'all 0.15s',
                        }}
                      >
                        {/* ⚠️ Le libellé de refus est réservé aux PDF réellement
                            invalides. La condition précédente (`!canStartAnalysis
                            && pdfValidation`) affichait « Trop volumineux » sur un
                            PDF parfaitement valide dès que le blocage venait du
                            projet — extraction en cours ou projet non validé. */}
                        {isValidating
                          ? t('upload_validating')
                          : projectExtracting
                            ? t('btn_project_extracting')
                            : pdfValidation?.valid === false
                              ? (pdfValidation.info?.validation_type === 'content' ? t('btn_invalid') : t('btn_too_large'))
                              : t('btn_analyze')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Norma V{APP_VERSION}</span>
          </div>

        </div>
      </div>

    </div>
    );
  };
  
  export default Dashboard;